import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAddress,
  clearError,
  clearSuccessMessage,
} from "../../redux/reducer/userAddressReducer";
import { useTranslation } from "react-i18next";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import CustomDropdownCountryAddress from "../sharedComponents/CustomDropdownCountryAddress";
import { getMenuItemsLocationTo } from "../../constent/countries";

const CustomBigButton = ({ addresses, userId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: "",
    city: "",
    postalCode: "",
    state: "",
    fullAddress: "",
  });

  const dispatch = useDispatch();
  const { loading, error, successMessage } = useSelector(
    (state) => state.userAddress,
  );

  // getMenuItemsLocationTo
  const menuItemsLocationTo = getMenuItemsLocationTo(t);

  // Sync userId with formData whenever it changes
  useEffect(() => {
    if (userId) {
      setFormData((prevData) => ({ ...prevData, userId }));
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (submitAttempted) {
      setSubmitAttempted(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !formData.country ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.city ||
      !formData.postalCode ||
      !formData.state ||
      !formData.fullAddress
    ) {
      setSubmitAttempted(true);
      return;
    }

    // Include userId in the payload
    if (userId) {
      dispatch(createAddress({ ...formData }))
        .unwrap()
        .then(() => {
          window.location.reload();
          closeModal();
        })
        .catch((err) => {
          console.error("Error adding address:", err);
        });
    } else {
      alert(t("requiredAddress"));
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSubmitAttempted(false);
    setFormData({
      firstName: "",
      lastName: "",
      fullAddress: "",
      country: "",
      city: "",
      postalCode: "",
      state: "",
    });
    dispatch(clearError());
    dispatch(clearSuccessMessage());
  };

  if (addresses?.length > 5) {
    return <div className="mt-4"></div>;
  }

  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="w-full px-4 py-2 font-medium text-black rounded-lg lg:text-lg bg-custom-yellow hover:bg-custom-yellow focus:outline-none focus:ring-2 focus:ring-custom-yellow"
      >
        {addresses?.length === 0 ? t("AddAddress") : t("Enteranotheraddress")}
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-2xl p-4 m-2 mt-8 bg-white rounded-lg shadow-lg lg:m-0 lg:mt-16 max-h-[80vh] overflow-scroll">
            <h2 className="mb-2 text-lg font-bold text-black">
              {t("EnterAddress")}
            </h2>
            {error && <p className="text-red-500">{error}</p>}
            <form onSubmit={handleSubmit}>
              <div className="p-2 mb-2 text-black bg-blue-100 rounded-sm">
                <p className="mb-2 text-sm lg:text-base">
                  {t("addressTitleNotes")}
                </p>
                <ul className="px-6 text-xs list-disc lg:text-sm">
                  <li>
                    {t("addressNotes1")}
                    <br />
                    {t("addressNotes2")}
                  </li>
                  <li>{t("addressNotes3")}</li>
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(formData).map((key, index) =>
                  key !== "userId" ? (
                    <div
                      key={index}
                      className={key === "fullAddress" ? "col-span-2" : ""}
                    >
                      <label
                        htmlFor={key}
                        className={`block ${
                          key === "country" ? "mb-0" : "mb-2"
                        } text-xs lg:text-sm font-semibold text-gray-700`}
                      >
                        {key === "fullAddress"
                          ? t("FullAddress")
                          : key === "city"
                            ? t("City")
                            : key === "postalCode"
                              ? t("PostalCode")
                              : key === "state"
                                ? t("State")
                                : key === "firstName"
                                  ? t("FirstName")
                                  : key === "lastName"
                                    ? t("LastName")
                                    : ""}
                        {key === "country" ? (
                          <></>
                        ) : (
                          <span className="ml-1 text-red-500">*</span>
                        )}
                      </label>
                      {key === "fullAddress" ? (
                        <textarea
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          className={`w-full h-20 px-2 py-1 text-black bg-white border rounded-lg lg:px-5 lg:py-3 placeholder:text-black lg:placeholder:text-sm placeholder:text-xs ${
                            submitAttempted && !formData[key]
                              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300"
                          }`}
                          rows={4}
                          required
                        />
                      ) : key === "country" ? (
                        <CustomDropdownCountryAddress
                          title={t("Country")}
                          menuItems={menuItemsLocationTo}
                          textColor="custom-gray"
                          onSelect={(item) => {
                            setFormData((prev) => ({
                              ...prev,
                              country: item.id,
                            }));
                            // Remove error when selecting
                            if (submitAttempted) {
                              setSubmitAttempted(false);
                            }
                          }}
                          selected={formData.country}
                          font={true}
                          add_addresses={true}
                          required={true}
                          showError={submitAttempted && !formData.country}
                          errorMessage={t("pleaseSelectCountry")}
                        />
                      ) : (
                        <input
                          type="text"
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          className={`w-full px-2 py-1 text-black bg-white border rounded-lg lg:px-5 lg:py-3 placeholder:text-black placeholder:text-sm ${
                            submitAttempted && !formData[key]
                              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300"
                          }`}
                          required
                        />
                      )}
                      {/* Error message for regular fields */}
                      {submitAttempted &&
                        !formData[key] &&
                        key !== "country" && (
                          <p className="mt-1 text-sm text-red-500">
                            {t("thisFieldIsRequired") || "هذا الحقل مطلوب"}
                          </p>
                        )}
                    </div>
                  ) : null,
                )}
              </div>
              <div className="flex justify-between mt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-bold text-black duration-300 bg-gray-300 lg:text-base hover:bg-gray-400 focus:outline-none"
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className={`${
                    loading
                      ? "bg-custom-yellow"
                      : "bg-custom-yellow hover:bg-custom-yellow"
                  } text-white font-bold  focus:outline-none focus:ring-2 focus:ring-custom lg:text-base text-sm`}
                  disabled={loading}
                >
                  {loading ? (
                    <ArrowPathIcon className="w-10 h-10 p-2 text-white rounded-full animate-spin bg-custom-yellow" />
                  ) : (
                    <button
                      type="submit"
                      className="px-4 py-2 text-black duration-300 bg-custom-yellow hover:bg-yellow-400"
                    >
                      {t("Submit")}
                    </button>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomBigButton;
